import { apiVersion } from '../apis.const';
import { transformResponse } from '../apis.util';
import { baseApi } from '../base.api';

/**
 * Create APIs
 */
export const notificationsApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    fetchNotifications: build.query<FetchNotificationsRes, FetchNotificationsReq>({
      query: ({ orgSlug }) => ({
        url: `/${orgSlug}/${apiVersion}/notifications`,
      }),
      transformResponse,
    }),

    fetchUserNotifications: build.query<FetchUserNotificationsRes, FetchUserNotificationsReq>({
      query: ({ orgSlug, userId }) => ({
        url: `/${orgSlug}/${apiVersion}/notifications/user/${userId}`,
      }),
      transformResponse,
      providesTags: (result, error, { userId }) => [{ type: 'UserNotifications', userId }],
    }),

    updateUserNotificationsPreference: build.mutation<
      UpdateUserNotificationsPreferenceRes,
      UpdateUserNotificationsPreferenceReq
    >({
      query: ({ orgSlug, userId, preference }) => ({
        url: `/${orgSlug}/${apiVersion}/notifications/user/${userId}/preference/${preference}`,
        method: 'PUT',
      }),
      transformResponse,
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserNotifications', userId }],
    }),

    deleteUserNotificationsPreference: build.mutation<
      DeleteUserNotificationsPreferenceRes,
      DeleteUserNotificationsPreferenceReq
    >({
      query: ({ orgSlug, userId, preference }) => ({
        url: `/${orgSlug}/${apiVersion}/notifications/user/${userId}/preference/${preference}`,
        method: 'DELETE',
      }),
      transformResponse,
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserNotifications', userId }],
    }),

    updateUserNotificationsChannel: build.mutation<
      UpdateUserNotificationsChannelRes,
      UpdateUserNotificationsChannelReq
    >({
      query: ({ orgSlug, userId, preference, channel }) => ({
        url: `/${orgSlug}/${apiVersion}/notifications/user/${userId}/preference/${preference}/${channel}`,
        method: 'PUT',
      }),
      transformResponse,
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserNotifications', userId }],
    }),

    deleteUserNotificationsChannel: build.mutation<
      DeleteUserNotificationsChannelRes,
      DeleteUserNotificationsChannelReq
    >({
      query: ({ orgSlug, userId, preference, channel }) => ({
        url: `/${orgSlug}/${apiVersion}/notifications/user/${userId}/preference/${preference}/${channel}`,
        method: 'DELETE',
      }),
      transformResponse,
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserNotifications', userId }],
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useFetchNotificationsQuery,
  useFetchUserNotificationsQuery,
  useUpdateUserNotificationsPreferenceMutation,
  useDeleteUserNotificationsPreferenceMutation,
  useUpdateUserNotificationsChannelMutation,
  useDeleteUserNotificationsChannelMutation,
} = notificationsApi;

/**
 * Types
 */
type OrgSlug = {
  orgSlug: string;
};
type FetchNotificationsRes = any;
type FetchNotificationsReq = OrgSlug;
type FetchUserNotificationsRes = any;
type UserNotificationsReq = OrgSlug & {
  userId: number;
};
type FetchUserNotificationsReq = UserNotificationsReq;
type UpdateUserNotificationsPreferenceRes = any;
type UserNotificationsPreferenceReq = UserNotificationsReq & {
  preference: number;
};
type UpdateUserNotificationsPreferenceReq = UserNotificationsPreferenceReq;
type DeleteUserNotificationsPreferenceRes = any;
type DeleteUserNotificationsPreferenceReq = UserNotificationsPreferenceReq;
type UpdateUserNotificationsChannelRes = any;
type UserNotificationsChannelReq = UserNotificationsPreferenceReq & {
  channel: string;
};
type UpdateUserNotificationsChannelReq = UserNotificationsChannelReq;
type DeleteUserNotificationsChannelRes = any;
type DeleteUserNotificationsChannelReq = UserNotificationsChannelReq;
